import {
  UPDATE_LOCATION_SELECT_OPTIONS,
  UPDATE_VEHICLE_INFO_OPTIONS,
  UPDATE_LOCATION_INFO,
  UPDATE_VEHICLE_INFO,
  UPDATE_TRANSACTION_INFO,
  UPDATE_FIRST_BUYER_INFO,
  UPDATE_INSURANCE_INFO,
  UPDATE_SECOND_SELLER_INFO,
  UPDATE_FIRST_SELLER_INFO,
  UPDATE_SECOND_BUYER_INFO,
  UPDATE_LIEN_HOLDER_INFO,
  UPDATE_FIRST_OWNER_INFO,
  UPDATE_SECOND_OWNER_INFO,
  CLEAR_FORM_DATA,
  SET_SELECTED_CARDS,
  GET_LIEN_HOLDER_INFO_SUCCESS
} from './types';

const INITIAL_STATE = {
  locationInfo: {
    zipCode: '',
    cities: [],
    states: [],
    countries: [],
    outSideCityLimit: false,
    city: '',
    state: '',
    country: '',
  },
  vehicleInfo: {
    vinCode: '',
    vehicleType: '',
    fuelType: '',
    make: '',
    gvw: '',
    vehicleWeight: '',
    msrp: '',
    modelYear: '',
    model: '',
    bodyStyle: '',
    numberOfAxles: '',
    numberOfTires: '',
    cylinders: '',
    condition: 'Used',
    registrationMonths: '',
    odometer: '',
    rimSize: '',
    color: '',
    mpg: '',
    // Broken-out MPG fields per ATC AGF Consumption Guide:
    //  <MPG>         -> cityMpg     (City miles per gallon)
    //  <HighwayMPG>  -> highwayMpg  (Highway miles per gallon)
    //  <CombinedMPG> -> combinedMpg (Combined miles per gallon)
    cityMpg: '',
    highwayMpg: '',
    combinedMpg: '',
    horsepower: '',
  },
  transactionInfo: {
    // Two distinct amounts, both sent to ATC under their own field name:
    //   basePrice  -> BasePrice  (the vehicle's base price)
    //   totalPrice -> TotalPrice (the transaction total)
    // basePrice used to be submitted as TotalPrice, which is why the online
    // report had no Total Price row at all and the bot's Total Price row was
    // really showing the base price.
    basePrice: '',
    totalPrice: '',
    plateType: 'Regular',
    transactionType: '',
    titleStatus: 'T',
    plateTransfer: false,
    transactionDate: '',
    uploadDate: '',
    driverCategory: '',
    plateNumber: '',
    docsFees: '',
    additionalFees: '',
    insurancePackages: '',
    protectionPackages: '',
    theftProtection: '',
    extendedWarranties: '',
    Discounts: '',
    military: false,
    lien: true,
  },
  firstBuyerInfo: {
    buyerFirstName: '',
    buyerMiddleName: '',
    buyerLastName: '',
    buyerCompanyName: '',
    buyerAddressLine1: '',
    buyerAddressLine2: '',
    buyerCity: '',
    buyerState: '',
    buyerZip: '',
    buyerDob: '',
    buyerSsn: '',
    buyerLicenseNumber: '',
    buyerLicenseState: '',
  },
  secondBuyerInfo: {
    secondBuyerFirstName: '',
    secondBuyerMiddleName: '',
    secondBuyerLastName: '',
    secondBuyerCompanyName: '',
    secondBuyerAddressLine1: '',
    secondBuyerAddressLine2: '',
    secondBuyerCity: '',
    secondBuyerState: '',
    secondBuyerZip: '',
    secondBuyerDob: '',
    secondBuyerSsn: '',
    secondBuyerLicenseNumber: '',
    secondBuyerLicenseState: '',
  },
  firstSellerInfo: {
    sellerFirstName: '',
    sellerMiddleName: '',
    sellerLastName: '',
    sellerCompany: '',
    sellerAddress: '',
    sellerCity: '',
    sellerState: '',
    sellerZip: '',
    sellerDealerNumber: '',
  },
  secondSellerInfo: {
    secondSellerFirstName: '',
    secondSellerMiddleName: '',
    secondSellerLastName: '',
    secondSellerCompany: '',
    secondSellerAddress: '',
    secondSellerCity: '',
    secondSellerState: '',
    secondSellerZip: '',
    secondSellerDealerNumber: '',
  },
  insuranceInfo: {
    insuranceCompany: '',
    insuranceDate: '',
    insuranceExpirationDate: '',
    insurancePolicyNumber: '',
  },
  lienHolderInfo: {
    lienholderCompanyName: '',
    lienholderAddress: '',
    lienholderAddress2: '',
    lienholderCity: '',
    lienholderState: '',
    lienholderZip: '',
    lienholderFederalTaxId: '',
    lienholderStateTaxId: '',
    lienholderEltName: '',
    lienholderEltId: '',
    lienholderEltAddress: '',
  },
  firstOwnerInfo: {
    ownerFirstName: '',
    ownerMiddleName: '',
    ownerLastName: '',
    ownerPhoneNumber: '',
    ownerCompanyName: '',
    ownerAddressLine1: '',
    ownerAddressLine2: '',
    ownerCity: '',
    ownerState: '',
    ownerCountry: '',
    ownerZip: '',
    ownerDob: '',
    ownerSsn: '',
    ownerLicenseNumber: '',
    ownerLicenseState: '',
  },
  secondOwnerInfo: {
    secondOwnerFirstName: '',
    secondOwnerMiddleName: '',
    secondOwnerLastName: '',
    secondOwnerPhoneNumber: '',
    secondOwnerCompanyName: '',
    secondOwnerAddressLine1: '',
    secondOwnerAddressLine2: '',
    secondOwnerCity: '',
    secondOwnerState: '',
    secondOwnerCountry: '',
    secondOwnerZip: '',
    secondOwnerDob: '',
    secondOwnerSsn: '',
    secondOwnerLicenseNumber: '',
    secondOwnerLicenseState: '',
  }
};

const formsReducer = (state = INITIAL_STATE, action) => {
  const { type, payload } = action;
  switch (type) {
    case UPDATE_LOCATION_SELECT_OPTIONS:
      return {
        ...state,
        locationInfo: {
          ...state.locationInfo,
          cities: payload.cities || [],
          states: payload.states || [],
          countries: payload.counties || [],
        },
      };

    case UPDATE_LOCATION_INFO:
      return {
        ...state,
        locationInfo: {
          ...state.locationInfo,
          [payload.key]: payload.value,
        },
      };

    case UPDATE_VEHICLE_INFO_OPTIONS:
      return {
        ...state,
        vehicleInfo: {
          ...state.vehicleInfo,
          vehicleType: payload.vehicle_type || '',
          fuelType: payload.fuel_type || '',
          gvw: payload.gvw || '',
          vehicleWeight: payload.weight || '',
          msrp: payload.msrp || '',
          modelYear: payload.model_year || '',
          cylinders: payload.cylinders  || '',
          make: payload.make || '',
          model: payload.model || '',
          bodyStyle: payload.bodyStyle || '',
          mpg: payload.mpg ? String(payload.mpg) : '',
          // The backend VIN-decode returns the three broken-out MPG values
          // straight from VINAudit (city/highway) plus a computed combined.
          // Falls back to the legacy `mpg` (combined) when those aren't present.
          cityMpg: payload.city_mpg !== undefined && payload.city_mpg !== null && payload.city_mpg !== ''
            ? String(payload.city_mpg)
            : '',
          highwayMpg: payload.highway_mpg !== undefined && payload.highway_mpg !== null && payload.highway_mpg !== ''
            ? String(payload.highway_mpg)
            : '',
          combinedMpg: payload.combined_mpg !== undefined && payload.combined_mpg !== null && payload.combined_mpg !== ''
            ? String(payload.combined_mpg)
            : (payload.mpg ? String(payload.mpg) : ''),
          vinsource: payload.source || 'VINAudit',
          registrationMonths: payload.registrationMonths || '12',
          numberOfAxles: payload.numberOfAxles || '2',
          numberOfTires: payload.numberOfTires || '4',
        },
      };

    case UPDATE_VEHICLE_INFO:
      return {
        ...state,
        vehicleInfo: {
          ...state.vehicleInfo,
          [payload.key]: payload.value,
        },
      };

    case UPDATE_TRANSACTION_INFO:
      return {
        ...state,
        transactionInfo: {
          ...state.transactionInfo,
          [payload.key]: payload.value,
        },
      };

    case UPDATE_FIRST_BUYER_INFO:
      return {
        ...state,
        firstBuyerInfo: {
          ...state.firstBuyerInfo,
          [payload.key]: payload.value,
        },
      };

    case UPDATE_SECOND_BUYER_INFO:
      return {
        ...state,
        secondBuyerInfo: {
          ...state.secondBuyerInfo,
          [payload.key]: payload.value,
        },
      };

    case UPDATE_FIRST_SELLER_INFO:
      return {
        ...state,
        firstSellerInfo: {
          ...state.firstSellerInfo,
          [payload.key]: payload.value,
        },
      };

    case UPDATE_SECOND_SELLER_INFO:
      return {
        ...state,
        secondSellerInfo: {
          ...state.secondSellerInfo,
          [payload.key]: payload.value,
        },
      };

    case UPDATE_INSURANCE_INFO:
      return {
        ...state,
        insuranceInfo: {
          ...state.insuranceInfo,
          [payload.key]: payload.value,
        },
      };

    case UPDATE_LIEN_HOLDER_INFO:
      return {
        ...state,
        lienHolderInfo: {
          ...state.lienHolderInfo,
          [payload.key]: payload.value,
        },
      };

    case UPDATE_FIRST_OWNER_INFO:
      return {
        ...state,
        firstOwnerInfo: {
          ...state.firstOwnerInfo,
          [payload.key]: payload.value,
        },
      };

    case UPDATE_SECOND_OWNER_INFO:
      return {
        ...state,
        secondOwnerInfo: {
          ...state.secondOwnerInfo,
          [payload.key]: payload.value,
        },
      };

    case CLEAR_FORM_DATA:
      return {
        ...state,
        locationInfo: INITIAL_STATE.locationInfo,
        vehicleInfo: INITIAL_STATE.vehicleInfo,
        transactionInfo: INITIAL_STATE.transactionInfo,
        firstBuyerInfo: INITIAL_STATE.firstBuyerInfo,
        secondBuyerInfo: INITIAL_STATE.secondBuyerInfo,
        firstSellerInfo: INITIAL_STATE.firstSellerInfo,
        secondSellerInfo: INITIAL_STATE.secondSellerInfo,
        insuranceInfo: INITIAL_STATE.insuranceInfo,
        lienHolderInfo: INITIAL_STATE.lienHolderInfo,
        firstOwnerInfo: INITIAL_STATE.firstOwnerInfo,
        secondOwnerInfo: INITIAL_STATE.secondOwnerInfo,
      }

    case SET_SELECTED_CARDS:
      return {
        ...state,
        selectedCards: action.payload,
      };

    case GET_LIEN_HOLDER_INFO_SUCCESS:
      return {
        ...state,
        lienHolderInfo: {
          ...state.lienHolderInfo,
          lienholderCompanyName: payload["Lienholder Company Name"] || '',
          lienholderAddress: payload["Lienholder Address 1"] || '',
          lienholderAddress2: payload["Lienholder Address 2"] || '',
          lienholderCity: payload["Lienholder City"] || '',
          lienholderState: payload["Lienholder State"] || '',
          lienholderZip: payload["Lienholder Zip"] || '',
          lienholderStateTaxId: payload["Lienholder State Tax ID"] || '',
          lienholderEltName: payload["Credit union name"] || '',
          lienholderEltId: payload["Investor ID"] || '',
          lienholderFederalTaxId: payload["Lienholder Federal ELT ID"] || '',
          lienholderEltAddress: payload["Lienholder ELT Address"] || '',
          lienholderEltState: payload["ELT State"] || '',
        },
      };

    default:
      return state;
  }
};

export default formsReducer;
